import React, {useEffect, useState} from 'react'

import Auth from "./Auth/Auth";
import LoadingSpinner from "./Components/Util/LoadingSpinner";
import ConfigContext from "./ConfigContext"

export const ConfigProvider = ({children}) => {

    const auth = new Auth()

    const version = VERSION
    const [config, setConfig] = useState(null)
    const [loading, setLoading] = useState({
        attachments: true,
        company: true,
        dashboard: true,
        documents: true,
        letters: true,
        map: true,
        permitTemplates: true,
        quarterlyWellAssessment: true,
        rainfall_site: true,
        rainfall_record: true,
        reports: true,
        user: true,
        video: true,
        waterLevels: true,
        water_quality: true,
        waterQualitySearchMappings: true,
        wellApplication: true,
        wellPermit: true,
        wellProduction: true,
        wellSearchMappings: true,
        wellSecondaryStatuses: true,
        wellStatuses: true,
        wellSpacing: true,
        wellUses: true
    })

    const isLoading = () => {
        return Object.values(loading).some((l) => l === true);
    }

    const load = async () => {
        setLoading(loading => ({
            ...loading,
            attachments: true,
            company: true,
            dashboard: true,
            documents: true,
            letters: true,
            map: true,
            permitTemplates: true,
            rainfall_site: true,
            rainfall_record: true,
            reports: true,
            user: true,
            video: true,
            waterLevels: true,
            water_quality: true,
            waterQualitySearchMappings: true,
            quarterlyWellAssessment: true,
            wellApplication: true,
            wellPermit: true,
            wellProduction: true,
            wellSearchMappings: true,
            wellSecondaryStatuses: true,
            wellStatuses: true,
            wellSpacing: true,
            wellUses: true
        }))

        const {default: HydrosClient} = await import(
            /* webpackChunkName: "hydros-client" */
            "./API/HydrosClient"
            );

        const hydrosClient = new HydrosClient();


        hydrosClient.Company.get().then((c) => {
            setConfig(data => ({...data, company: c}))
            setLoading(loading => ({...loading, company: false}))
        }).catch(() => {
            setConfig(data => ({...data, company: {}}))
            setLoading(loading => ({...loading, company: false}))
        })
        hydrosClient.Config.get('attachments').then((c) => {
            setConfig(data => ({...data, attachments: c}))
            setLoading(loading => ({...loading, attachments: false}))
        }).catch(() => {
            setConfig(data => ({...data, attachments: {}}))
            setLoading(loading => ({...loading, attachments: false}))
        })
        hydrosClient.Config.get('dashboard').then((c) => {
            setConfig(data => ({...data, dashboard: c}))
            setLoading(loading => ({...loading, dashboard: false}))
        }).catch(() => {
            setConfig(data => ({...data, dashboard: {}}))
            setLoading(loading => ({...loading, dashboard: false}))
        })
        hydrosClient.Documents.list().then((d) => {
            setConfig(data => ({...data, documents: d}))
        }).catch(() => {
            setConfig(data => ({...data, documents: []}))
        }).finally(() => setLoading(loading => ({...loading, documents: false})))
        hydrosClient.Config.get('letters').then((c) => {
            setConfig(data => ({...data, letters: c}))
            setLoading(loading => ({...loading, letters: false}))
        }).catch(() => {
            setConfig(data => ({...data, letters: {}}))
            setLoading(loading => ({...loading, letters: false}))
        })
        hydrosClient.Config.get('map').then((c) => {
            setConfig(data => ({...data, map: c}))
            setLoading(loading => ({...loading, map: false}))
        }).catch(() => {
            setConfig(data => ({...data, map: {}}))
            setLoading(loading => ({...loading, map: false}))
        })

        if (auth.hasPermission('permitting:write') || auth.hasPermission('permitting:read')) {
            setLoading(loading => ({...loading, permitTemplates: true}))

            hydrosClient.PermitTemplates.list({sort: {field: 'permitName', direction: 'asc'}})
                .then((response) => {
                    setConfig(data => ({...data, permitTemplates: response}))
                    setLoading(loading => ({...loading, permitTemplates: false}))
                })
                .catch(() => {
                    setConfig(data => ({...data, permitTemplates: {}}))
                    setLoading(loading => ({...loading, permitTemplates: false}))
                })
        } else {
            setLoading(loading => ({...loading, permitTemplates: false}))
        }

        if (auth.hasPermission('rainfall:read') || auth.hasPermission('rainfall:write')) {
            hydrosClient.Config.get('rainfall_site').then((c) => {
                setConfig(data => ({...data, rainfall_site: c}))
                setLoading(loading => ({...loading, rainfall_site: false}))
            }).catch(() => {
                setConfig(data => ({...data, rainfall_site: {}}))
                setLoading(loading => ({...loading, rainfall_site: false}))
            })
        } else {
            setLoading(loading => ({...loading, rainfall_site: false}))
        }

        if (auth.hasPermission('rainfall:read') || auth.hasPermission('rainfall:write')) {
            hydrosClient.Config.get('rainfall_record').then((c) => {
                setConfig(data => ({...data, rainfall_record: c}))
                setLoading(loading => ({...loading, rainfall_record: false}))
            }).catch(() => {
                setConfig(data => ({...data, rainfall_record: {}}))
                setLoading(loading => ({...loading, rainfall_record: false}))
            })
        } else {
            setLoading(loading => ({...loading, rainfall_record: false}))
        }

        hydrosClient.Config.get('reports').then((c) => {
            setConfig(data => ({...data, reports: c}))
            setLoading(loading => ({...loading, reports: false}))
        }).catch(() => {
            setConfig(data => ({...data, reports: {}}))
            setLoading(loading => ({...loading, reports: false}))
        })

        hydrosClient.Users.get(auth.getClaim('hydros_user_id')).then((c) => {
            setConfig(data => ({...data, user: c}))
            setLoading(loading => ({...loading, user: false}))
        }).catch(() => {
            setConfig(data => ({...data, user: {}}))
            setLoading(loading => ({...loading, user: false}))
        })

        if (auth.hasPermission('video:read') || auth.hasPermission('video:write')) {
            hydrosClient.Config.get('video').then((c) => {
                setConfig(data => ({...data, video: c}))
                setLoading(loading => ({...loading, video: false}))
            }).catch(() => {
                setConfig(data => ({...data, video: {}}))
                setLoading(loading => ({...loading, video: false}))
            })
        } else {
            setLoading(loading => ({...loading, video: false}))
        }

        // Always load water_levels as we have public monitoring wells layer and public hydrograph
        hydrosClient.Config.get('water_levels').then((c) => {
            setConfig(data => ({...data, waterLevels: c}))
            setLoading(loading => ({...loading, waterLevels: false}))
        }).catch(() => {
            setConfig(data => ({...data, waterLevels: {}}))
            setLoading(loading => ({...loading, waterLevels: false}))
        })

        if (auth.hasPermission('water_quality:read') || auth.hasPermission('water_quality:write')) {
            hydrosClient.Config.get('water_quality').then((c) => {
                setConfig(data => ({...data, water_quality: c}))
                setLoading(loading => ({...loading, water_quality: false}))
            }).catch(() => {
                setConfig(data => ({...data, water_quality: {}}))
                setLoading(loading => ({...loading, water_quality: false}))
            })

            hydrosClient.WaterQuality.getSearchMappings().then((c) => {
                setConfig(data => ({...data, waterQualitySearchMappings: c}))
                setLoading(loading => ({...loading, waterQualitySearchMappings: false}))
            }).catch(() => {
                setConfig(data => ({...data, waterQualitySearchMappings: {}}))
                setLoading(loading => ({...loading, waterQualitySearchMappings: false}))
            })
        } else {
            setLoading(loading => ({...loading, water_quality: false, waterQualitySearchMappings: false}))
        }

        hydrosClient.Config.get('pws/quarterly_well_assessment').then((c) => {
            setConfig(data => ({...data, quarterlyWellAssessment: c}))
            setLoading(loading => ({...loading, quarterlyWellAssessment: false}))
        }).catch(() => {
            setConfig(data => ({...data, quarterlyWellAssessment: {}}))
            setLoading(loading => ({...loading, quarterlyWellAssessment: false}))
        })
        hydrosClient.Config.get('well/application').then((c) => {
            setConfig(data => ({...data, wellApplication: c}))
            setLoading(loading => ({...loading, wellApplication: false}))
        }).catch(() => {
            setConfig(data => ({...data, wellApplication: {}}))
            setLoading(loading => ({...loading, wellApplication: false}))
        })
        hydrosClient.Config.get('well/permit').then((c) => {
            setConfig(data => ({...data, wellPermit: c}))
            setLoading(loading => ({...loading, wellPermit: false}))
        }).catch(() => {
            setConfig(data => ({...data, wellPermit: {}}))
            setLoading(loading => ({...loading, wellPermit: false}))
        })
        hydrosClient.Config.get('well/production').then((c) => {
            setConfig(data => ({...data, wellProduction: c}))
            setLoading(loading => ({...loading, wellProduction: false}))
        }).catch(() => {
            setConfig(data => ({...data, wellProduction: {}}))
            setLoading(loading => ({...loading, wellProduction: false}))
        })
        hydrosClient.Config.get('well/spacing').then((c) => {
            setConfig(data => ({...data, wellSpacing: c}))
            setLoading(loading => ({...loading, wellSpacing: false}))
        }).catch(() => {
            setConfig(data => ({...data, wellSpacing: {}}))
            setLoading(loading => ({...loading, wellSpacing: false}))
        })
        hydrosClient.Wells.getSearchMappings().then((c) => {
            setConfig(data => ({...data, wellSearchMappings: c}))
            setLoading(loading => ({...loading, wellSearchMappings: false}))
        }).catch(() => {
            setConfig(data => ({...data, wellSearchMappings: {}}))
            setLoading(loading => ({...loading, wellSearchMappings: false}))
        })
        hydrosClient.Wells.getStatuses().then((c) => {
            setConfig(data => ({...data, wellStatuses: c}))
            setLoading(loading => ({...loading, wellStatuses: false}))
        }).catch(() => {
            setConfig(data => ({...data, wellStatuses: {}}))
            setLoading(loading => ({...loading, wellStatuses: false}))
        })
        hydrosClient.Wells.getSecondaryStatuses().then((c) => {
            setConfig(data => ({...data, wellSecondaryStatuses: c}))
            setLoading(loading => ({...loading, wellSecondaryStatuses: false}))
        }).catch(() => {
            setConfig(data => ({...data, wellSecondaryStatuses: {}}))
            setLoading(loading => ({...loading, wellSecondaryStatuses: false}))
        })
        hydrosClient.Wells.getWellUses().then((c) => {
            setConfig(data => ({...data, wellUses: c}))
            setLoading(loading => ({...loading, wellUses: false}))
        }).catch(() => {
            setConfig(data => ({...data, wellUses: {}}))
            setLoading(loading => ({...loading, wellUses: false}))
        })
    }

    useEffect(() => {
        load()
    }, [])

    if (isLoading())
        return (
            <div style={{padding: '100px 0 0 0'}}>
                <LoadingSpinner size={30}/>
            </div>
        )

    return (
        <ConfigContext.Provider value={{auth, loading, config, version}}>
            {children}
        </ConfigContext.Provider>
    )
}